import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { RegExp } from 'src/app/core/config/regex';
import { Course, CourseTest, CourseTestQuestionnaire, QuestionCategory } from 'src/app/shared/models/course';
import { Message } from 'src/app/shared/models/domain';
import { CommonService } from 'src/app/shared/services/common.service';
import { CourseService } from 'src/app/shared/services/course.service';

@Component({
  selector: 'app-course-test-crud',
  templateUrl: './course-test-crud.component.html',
  styleUrls: ['./course-test-crud.component.scss']
})
export class CourseTestCrudComponent implements OnInit {

  @Input() selectedCourseId: string;
  @Input() courseTest: CourseTest;
  @Input() courseTests: CourseTest[] = [];
  public questionCategories: QuestionCategory[] = [];
  @Output() passEntry: EventEmitter<any> = new EventEmitter();
  public courseTestQuestionnaireForm: FormGroup;
  public courseTestForm: FormGroup;
  public submitted: boolean = false;
  private modified: boolean = false;
  public statuses: string[] = [];
  public courses: Course[] = [];

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private commonService: CommonService,
    private courseService: CourseService,
    private toaster: ToastrService,
    private datePipe: DatePipe,
  ) {
  }

  ngOnInit(): void {
    this.setTestForm(this.courseTest);
    if (this.courseTest?.id) {
      this.getCourseTestQuestionnaires(this.courseTest?.id)
    }
    this.setCourseTestQuestionnaires([]);
    this.courseService.getQuestionCategories(false).subscribe((questionCategories: QuestionCategory[]) => {
      this.questionCategories = questionCategories;
    });
    this.getApprovalStatus();
    this.courseService.getCourses(false).subscribe((courses: Course[]) => {
      this.courses = courses;
    });
  }

  public setTestForm(courseTest?: CourseTest) {
    if (!courseTest) {
      courseTest = {
        id: null,
        code: '',
        name: '',
        title: '',
        label: '',
        tags: '',
        formula: '',
        passingScore: null,
        disabled: false,
        startDate: '',
        endDate: '',
        approvalStatus: "DRAFT",
        courseId: parseInt(this.selectedCourseId),
        courseName: ''
      } as CourseTest;
    }
    this.courseTestForm = this.fb.group({
      id: this.fb.control(courseTest.id),
      code: this.fb.control(courseTest.code?.toUpperCase() || '', [Validators.required, Validators.pattern(/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d-]+$/)]),
      name: this.fb.control(courseTest.name, [Validators.required, Validators.pattern(RegExp.name)]),
      title: this.fb.control(courseTest.title, [Validators.required, Validators.pattern(RegExp.label)]),
      label: this.fb.control(courseTest.label, [Validators.pattern(RegExp.label)]),
      tags: this.fb.control(courseTest.tags),
      formula: this.fb.control(courseTest.formula),
      passingScore: this.fb.control(courseTest.passingScore, [Validators.min(0), Validators.max(100), Validators.required]),
      disabled: this.fb.control(courseTest.disabled),
      startDate: this.fb.control(this.datePipe.transform(courseTest?.startDate || new Date(), 'yyyy-MM-dd'), [Validators.required]),
      endDate: this.fb.control(this.datePipe.transform(courseTest.endDate, 'yyyy-MM-dd'), [Validators.required]),
      approvalStatus: this.fb.control(courseTest.approvalStatus, [Validators.required]),
      courseId: this.fb.control(courseTest.courseId, [Validators.required]),
      courseName: this.fb.control(courseTest.courseName)
    });
    this.courseTestForm.valueChanges.subscribe(value => this.modified = true);
  }

  private getApprovalStatus() {
    this.courseService.getApprovalStatus().subscribe((statuses) => {
      this.statuses = statuses;
    });
  }

  get f() {
    return this.courseTestForm.controls;
  }

  get p() {
    return this.courseTestQuestionnaireForm.controls;
  }

  public saveCourseTest(inProgress?: any) {
    this.submitted = true;
    if (this.courseTestForm.valid) {
      const courseTest = this.courseTestForm.value as CourseTest;
      if (!this.modified) {
        this.activeModal.close(false);
        this.commonService.setToastr(200, `Course Test has been updated successfully!`);
        return;
      }
      inProgress.value = true;
      if (courseTest.id) {
        this.courseService.updateCourseTest(courseTest.id, courseTest).subscribe({
          next: (response: Message) => {
            if (!response.failed) {
              courseTest.courseId = this.f.id.value;
              this.passEntry.emit(response);
              this.toaster.success(response.text);
              inProgress.value = false;
            } else {
              inProgress.value = false;
              this.commonService.setToastr(500, response.text);
            }
            this.submitted = false;
          },
          error: (error: HttpErrorResponse) => {
            inProgress.value = false;
          },
        });
      } else {
        this.courseService.createCourseTest(courseTest).subscribe({
          next: (response: Message) => {
            if (!response.failed) {
              const courseTestId = parseInt(response.payload);
              this.f.id.setValue(courseTestId);
              this.courseTestForm.get("id").setValue(courseTestId);
              this.passEntry.emit(response);
              this.toaster.success(response.text);
              inProgress.value = false;
            } else {
              inProgress.value = false;
              this.commonService.setToastr(500, response.text);
            }
            this.submitted = false;
          },
          error: (error: HttpErrorResponse) => {
            inProgress.value = false;
          },
        });
      }
    }
  }

  get courseTestQuestionnaires(): FormArray {
    return this.courseTestQuestionnaireForm.get("courseTestQuestionnaires") as FormArray;
  }

  private setCourseTestQuestionnaires(courseTestQuestionnaires: CourseTestQuestionnaire[]) {
    this.courseTestQuestionnaireForm = this.fb.group({
      courseTestQuestionnaires: this.fb.array([])
    });
    if (courseTestQuestionnaires.length > 0) {
      courseTestQuestionnaires.forEach(l => this.addCourseTestQuestionnaire(l));
    }
    this.addCourseTestQuestionnaire();
  }

  private addCourseTestQuestionnaire(courseTestQuestionnaire?: CourseTestQuestionnaire) {
    this.courseTestQuestionnaires.push(this.fb.group({
      id: this.fb.control(courseTestQuestionnaire?.id || null, []),
      courseTestId: this.fb.control(courseTestQuestionnaire?.courseTestId || this.f.id.value, []),
      questionCategoryId: this.fb.control(courseTestQuestionnaire?.questionCategoryId, [Validators.required]),
      questionCategoryName: this.fb.control(courseTestQuestionnaire?.questionCategoryName, []),
      weight: this.fb.control(courseTestQuestionnaire?.weight, [Validators.required]),
      maxQuestion: this.fb.control(courseTestQuestionnaire?.maxQuestion, [Validators.required]),
      disabled: this.fb.control(courseTestQuestionnaire?.disabled || false, []),
    }));
  }

  public upsertCourseTestQuestionnaire(courseTestQuestionnaire: any, index: number) {
    if (courseTestQuestionnaire.valid) {
      let testQuestionnaire = courseTestQuestionnaire.value as CourseTestQuestionnaire;
      testQuestionnaire.courseTestId = this.f.id.value;
      if (testQuestionnaire.weight < testQuestionnaire.maxQuestion) {
        this.toaster.error('Weight value should not be less than the maximum number of questions.');
        return;
      }
      courseTestQuestionnaire.isUpdateInProgress = true;
      if (testQuestionnaire.id) {
        this.courseService.updateCourseTestQuestionnaire(testQuestionnaire.id, testQuestionnaire).subscribe({
          next: (message: Message) => {
            if (!message.failed) {
              if (this.courseTestQuestionnaires.length < 1) this.addCourseTestQuestionnaire();
              this.toaster.success(message.text);
            } else {
              this.commonService.setToastr(500, message.text);
            }
            courseTestQuestionnaire.isUpdateInProgress = false;
          },
          error: (error: HttpErrorResponse) => {
            this.toaster.error(error?.error?.name || error.message);
            courseTestQuestionnaire.isUpdateInProgress = false;
          }
        });
      } else {
        this.courseService.createCourseTestQuestionnaire(testQuestionnaire).subscribe({
          next: (message: Message) => {
            if (!message.failed) {
              this.courseTestQuestionnaires.at(index).patchValue({ id: parseInt(message.payload) });
              this.addCourseTestQuestionnaire();
              this.toaster.success(message.text);
            } else {
              this.commonService.setToastr(500, message.text);
            }
            courseTestQuestionnaire.isUpdateInProgress = false;
          },
          error: (error: HttpErrorResponse) => {
            this.toaster.error(error?.error?.name || error.message);
            courseTestQuestionnaire.isUpdateInProgress = false;
          }
        });
      }
    } else {
      this.toaster.error('Please enter valid course test questionnaire details');
    }
  }

  public deleteCourseTestQuestionnaire(courseTestQuestionnaire: any, i: number) {
    if (courseTestQuestionnaire.valid) {
      const courseTestQuestionnaireValue = courseTestQuestionnaire.value as CourseTestQuestionnaire;
      courseTestQuestionnaireValue.courseTestId = this.f.id.value;
      if (courseTestQuestionnaireValue.id) {
        courseTestQuestionnaire.isDeleteInProgress = true;
        this.courseService.deleteCourseTestQuestionnaire(courseTestQuestionnaireValue.id).subscribe({
          next: (message: Message) => {
            if (!message.failed) {
              this.courseTestQuestionnaires.removeAt(i);
              this.commonService.setToastr(200, message.text);
              courseTestQuestionnaire.isDeleteInProgress = false;
            }
          },
          error: (error: HttpErrorResponse) => {
            courseTestQuestionnaire.isDeleteInProgress = false;
          }
        });
      } else {
        this.courseTestQuestionnaires.removeAt(i);
        if (this.courseTestQuestionnaires.length < 1) {
          this.addCourseTestQuestionnaire();
        }
      }
    }
  }

  private getCourseTestQuestionnaires(courseId: number): void {
    if (courseId) this.courseService.getCourseTestQuestionnaires(courseId).subscribe(courseSlide => { this.setCourseTestQuestionnaires(courseSlide) });
  }

  public onCategoryChange(event: any, index: number): void {
    const selectedCategoryId = event && event.id ? event.id : null;
    if (selectedCategoryId) {
      const questionnaire = this.courseTestQuestionnaires.at(index) as FormGroup;
      this.courseService.getQuestionnaireCount(this.f.courseId?.value, selectedCategoryId).subscribe({
        next: (count) => {
          questionnaire.get('weight')?.setValue(count);
        },
        error: (error) => {
        }
      });
    }
  }

  public getBadgeClassForStatus(approvalStatus: string): string {
    switch (approvalStatus?.toUpperCase()) {
      case 'DRAFT':
        return 'badge bg-secondary text-white rounded-pill';
      case 'IN_REVIEW':
        return 'badge bg-primary text-white rounded-pill';
      case 'RELEASED':
        return 'badge bg-success text-white rounded-pill';
      case 'REVIEWED':
        return 'badge bg-warning text-dark rounded-pill';
      default:
        return 'badge bg-light text-dark rounded-pill';
    }
  }
  
  public formatStatus(label: string): string {
    return label
      ?.toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  }

}
